import React, { useState, useEffect, useRef } from "react";
import { Card, Row, Col, Button } from "react-bootstrap";
import { ActionHandlerFunction, IActionHandler, AdminUserBase } from "@preveil-api";
import { Account, AdminUIActionTypes, UserListActionTypes, isSameUser, GatewayConnectivityStatus, Message, AdminMessages, MessageHandlerDisplayType, useAppDispatch, MessageToastTypes } from "src/common";
import { InfoPopover, UserList } from "src/components";
import { uiActions } from "src/store";

type AllProps = {
  org_users: AdminUserBase[];
  current_account: Account;
  gateway_user_id: string[];
  gateway_connectivity_status?: GatewayConnectivityStatus;
  is_invalid_gateway_id: boolean;
  is_email_gateway_enabled: boolean;
  handleAction: ActionHandlerFunction;
}

function TopPanelComponent(props: AllProps) {
  const { current_account, handleAction, gateway_user_id, gateway_connectivity_status, is_invalid_gateway_id, org_users, is_email_gateway_enabled } = props;
  const dispatch = useAppDispatch();
  const [show_input, setShowInput] = useState<boolean>(false);
  const [current_gateway_user, setCurrentGatewayUser] = useState<string[]>(gateway_user_id);
  const [is_assign_btn_disabled, setIsAssignBtnDisabled] = useState<boolean>(true);
  const user_list_ref = useRef<{ handlePageActions: ActionHandlerFunction }>();
  const gateway_user_id_ref = useRef<string>();
  const is_healthy = !!gateway_connectivity_status?.healthy;
  const status = is_healthy ? "Ok" : gateway_connectivity_status?.details || "Connectivity Error.";
  const show_assign_btn = !show_input && !isSameUser(gateway_user_id[0], current_gateway_user[0]) || gateway_user_id.length === 0;

  useEffect(() => {
    if (gateway_user_id && gateway_user_id.length === 1) {
      setCurrentGatewayUser(gateway_user_id);
      // Sync the UserList component's internal state
      updateUserListState(gateway_user_id[0]);
    } else {
      setCurrentGatewayUser([]);
      // Clear the UserList when gateway_user_id is empty
      updateUserListState("");
    }
  }, [gateway_user_id]);

  useEffect(() => {
    if (is_invalid_gateway_id && gateway_user_id.length === 1 && !isSameUser(gateway_user_id_ref.current, gateway_user_id[0])) {
      gateway_user_id_ref.current = gateway_user_id[0];
      handleCancel();
    }
  }, [current_gateway_user]);

  // Description: This function will handle the gateway user action.
  function handleGatewayUser(action_obj: IActionHandler) {
    const { params } = action_obj;
    const { current_contacts } = params;
    if (current_contacts.length === 0) {
      if (show_input) {
        // if show_input is already true, then not need to set/change state.
        return;
      }
      setCurrentGatewayUser([]);
      setIsAssignBtnDisabled(true);
      setShowInput(true);
    } else if (current_contacts.length !== 0) {
      const { address } = params.current_contacts[0];
      gateway_user_id_ref.current = address;

      setCurrentGatewayUser([address]);
      if (show_input && gateway_user_id[0] !== address) {
        setIsAssignBtnDisabled(false);
        setShowInput(false);
      } else {
        setShowInput(false);
      }
    }
  }

  // Description: Update the UserList component state when the gateway_user_id changes.
  function updateUserListState(user_id: string) {
    if (user_list_ref.current) {
      user_list_ref.current.handlePageActions({
        actionType: 'SyncGatewayUser',
        params: { user_id }
      });
    }
  }

  // Description: Confirm dialog before removing the gateway user ID.
  function confirmRemoveGatewayUser() {
    const confirmation_dialog = new Message(
      AdminMessages.confirm_remove_gateway_user_id.body,
      MessageHandlerDisplayType.confirm,
      MessageToastTypes.primary,
      AdminMessages.confirm_remove_gateway_user_id.title,
      {
        label: "Yes",
        data: true,
        action: () => handleAction({ actionType: AdminUIActionTypes.RemoveGatewayUserId })
      },
      {
        label: "No"
      }
    );
    dispatch(uiActions.handleSetMessage(confirmation_dialog));
  }

  // Description: Cancel button will reset the gatewayUserId to the default value.
  function handleCancel() {
    const user_id = gateway_user_id[0];
    if (!!user_list_ref && !!user_list_ref.current) {
      user_list_ref.current.handlePageActions({
        actionType: UserListActionTypes.Cancel,
        params: {
          user_id
        }
      });
    }
  }

  return <Card.Body>
    <Row>
      <Col sm={12} md={6}>
        <p>Relay User ID
          <InfoPopover
            message="The relay user is a <b>required</b> service account containing credentials to secure routing relay emails." />
        </p>
        <div className="mt-1 d-flex">
          <div className={`gateway-user-list ${show_input ? "show" : "hide"}`}>
            <UserList
              ref={user_list_ref}
              single_user
              org_users={org_users}
              current_account={current_account}
              handleAction={handleGatewayUser}
              user_ids={current_gateway_user}
              placeholder="Start typing to add a user"
            />
          </div>
          {show_input && gateway_user_id.length !== 0 ? (
            <Button variant="no-outline-secondary" size="sm" onClick={handleCancel}>
              Cancel
            </Button>
          ) : null}
          {show_assign_btn && (
            <Button
              disabled={is_assign_btn_disabled}
              variant="outline-secondary"
              size="sm"
              className="action-btn"
              onClick={() => {
                handleAction({
                  actionType: AdminUIActionTypes.ChangeGatewayUserId,
                  params: current_gateway_user[0],
                });
              }}>Assign</Button>
          )}
          {!show_input && !is_email_gateway_enabled && gateway_user_id.length !== 0 && (
            <Button
              variant="outline-secondary"
              size="sm"
              className="action-btn"
              onClick={confirmRemoveGatewayUser}>Remove</Button>
          )}
        </div>
      </Col>
      <Col sm={12} md={6} className="mt-4 mt-md-0">
        <p>
          Connectivity Test
          <InfoPopover
            placement="bottom-end"
            message="Connectivity test will check that the Relay can send and receive mail to and from the PreVeil service in your Org. If errors are shown, they must be resolved before Relay will operate correctly." />
        </p>
        <div className="gateway-status">
          <p className={`fs-5 ${is_healthy ? "success-green" : "error-red"}`}>{status}</p>
          <Button variant="outline-secondary" className="btn-sm action-btn" disabled={gateway_user_id.length === 0} onClick={() => handleAction({ actionType: AdminUIActionTypes.TestGatewayConnectivity })}>
            Test Now
          </Button>
        </div>
      </Col>
    </Row>
  </Card.Body>;
}

export default React.memo(TopPanelComponent);
